import React, { useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Code,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
  Tooltip,
  Transition,
} from '@mantine/core';
import { IconCopy, IconJson, IconPlayerPause, IconPlayerPlay, IconTrash, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useRealtimeAuditLogs } from '../../../entities/SystemAuditLog/queries/useRealtimeAuditLogs';
import { IAuditLogItem } from '../../../entities/SystemAuditLog/SystemAuditLog';
import { notifications } from '@mantine/notifications';

interface AuditLogDrawerProps {
  opened: boolean;
  onClose: () => void;
}

const PANEL_HEIGHT = 280;

const getActionColor = (action: string): string => {
  switch (action?.toLowerCase()) {
    case 'created':
      return 'green';
    case 'modified':
      return 'blue';
    case 'deactivated':
    case 'deleted':
      return 'red';
    case 'revoked':
      return 'orange';
    case 'login':
      return 'cyan';
    default:
      return 'gray';
  }
};

const AuditLogDrawer: React.FC<AuditLogDrawerProps> = ({ opened, onClose }) => {
  const { t } = useTranslation();
  const [isPolling, setIsPolling] = useState(true);
  const [detailItem, setDetailItem] = useState<IAuditLogItem | null>(null);
  const [clearedAt, setClearedAt] = useState<Date | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useRealtimeAuditLogs(opened && isPolling);

  useEffect(() => {
    if (opened) 
      setIsPolling(true);
    
    document.documentElement.style.setProperty('--audit-panel-height', opened ? `${PANEL_HEIGHT}px` : '0px');
    return () => {
      document.documentElement.style.setProperty('--audit-panel-height', '0px');
    };
  }, [opened]);

  const displayedItems = clearedAt
    ? data?.items?.filter((item) => new Date(item.eventDate) > clearedAt) ?? []
    : data?.items ?? [];

  const handleClear = () => {
    setClearedAt(new Date());
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    notifications.show({
      title: t('notification.success'),
      message: t('auditLog.detailsCopied'),
      color: 'green',
      autoClose: 2000,
    });
  };

  return (
    <>
      <Transition mounted={opened} transition="slide-up" duration={300}>
        {(styles) => (
          <Paper
            style={{
              ...styles,
              position: 'fixed',
              bottom: 0,
              left: 'var(--navbar-width, 0px)',
              right: 0,
              height: PANEL_HEIGHT,
              zIndex: 100,
              borderTop: '2px solid var(--mantine-color-blue-5)',
              borderRadius: 0,
            }}
            shadow="xl"
            p="xs"
          >
            <Stack gap={0} h="100%">
              <Group justify="space-between" px="xs" pb={6}>
                <Group gap="sm">
                  <Title order={5}>{t('auditLog.realtime.title')}</Title>
                  <Tooltip label={isPolling ? t('auditLog.realtime.pause') : t('auditLog.realtime.resume')}>
                    <ActionIcon
                      variant="light"
                      color={isPolling ? 'blue' : 'gray'}
                      onClick={() => setIsPolling(!isPolling)}
                      size="sm"
                    >
                      {isPolling ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
                    </ActionIcon>
                  </Tooltip>
                  {isPolling && (
                    <Badge size="xs" variant="dot" color="green">
                      {t('auditLog.realtime.live')}
                    </Badge>
                  )}
                </Group>
                <Group gap="xs">
                  <Tooltip label={t('auditLog.realtime.clear')}>
                    <ActionIcon variant="light" color="gray" onClick={handleClear} size="sm">
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <ActionIcon variant="subtle" color="gray" onClick={onClose} size="sm">
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              </Group>
              <ScrollArea h={PANEL_HEIGHT - 50} ref={scrollAreaRef}>
                {isLoading ? (
                  <Stack align="center" pt="md">
                    <Loader size="sm" />
                  </Stack>
                ) : (
                  <Stack gap={4} px="xs">
                    {displayedItems.length === 0 && (
                      <Text size="sm" c="dimmed" ta="center" pt="md">
                        {t('auditLog.realtime.noEntries')}
                      </Text>
                    )}
                    {displayedItems.map((item) => (
                      <Group
                        key={item.id}
                        gap="xs"
                        p={6}
                        wrap="nowrap"
                        style={{
                          borderRadius: 'var(--mantine-radius-sm)',
                          backgroundColor: 'var(--mantine-color-gray-0)',
                          borderLeft: `3px solid var(--mantine-color-${getActionColor(item.action)}-5)`,
                        }}
                      >
                        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                          {new Date(item.eventDate).toLocaleTimeString()}
                        </Text>
                        <Badge size="xs" variant="light" style={{ flexShrink: 0 }}>
                          {item.category}
                        </Badge>
                        <Badge size="xs" color={getActionColor(item.action)} variant="dot" style={{ flexShrink: 0 }}>
                          {item.action}
                        </Badge>
                        {item.entityType && (
                          <Text size="xs" c="dimmed" truncate>
                            {item.entityType}
                            {item.entityId ? ` #${item.entityId}` : ''}
                          </Text>
                        )}
                        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                          {item.actorType === 'System' ? 'System' : `User #${item.actorUserId}`}
                        </Text>
                        {item.details && (
                          <Tooltip label={t('auditLog.viewDetails')}>
                            <ActionIcon size="xs" variant="subtle" onClick={() => setDetailItem(item)} style={{ flexShrink: 0 }}>
                              <IconJson size={12} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    ))}
                  </Stack>
                )}
              </ScrollArea>
            </Stack>
          </Paper>
        )}
      </Transition>

      <Modal
        opened={detailItem !== null}
        onClose={() => setDetailItem(null)}
        title={t('auditLog.detailsTitle')}
        size="lg"
      >
        {detailItem && (
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={4}>{t('auditLog.details')}</Title>
              <ActionIcon
                variant="light"
                onClick={() => copyToClipboard(JSON.stringify(detailItem.details, null, 2))}
                title={t('button.copy')}
              >
                <IconCopy size={16} />
              </ActionIcon>
            </Group>
            <Code block size="sm" style={{ maxHeight: '400px', overflow: 'auto' }}>
              {JSON.stringify(detailItem.details, null, 2)}
            </Code>
            <Stack gap="xs">
              <Group>
                <Text fw={500} size="sm">{t('auditLog.eventId')}:</Text>
                <Text size="sm">{detailItem.id}</Text>
              </Group>
              <Group>
                <Text fw={500} size="sm">{t('auditLog.eventDate')}:</Text>
                <Text size="sm">{new Date(detailItem.eventDate).toLocaleString()}</Text>
              </Group>
              <Group>
                <Text fw={500} size="sm">{t('auditLog.category')}:</Text>
                <Badge size="sm">{detailItem.category}</Badge>
              </Group>
              <Group>
                <Text fw={500} size="sm">{t('auditLog.action')}:</Text>
                <Badge size="sm" color={getActionColor(detailItem.action)}>{detailItem.action}</Badge>
              </Group>
            </Stack>
          </Stack>
        )}
      </Modal>
    </>
  );
};

export default AuditLogDrawer;
